/**
 * Accounts on the phones, by direction (plan Stage 1, owner 2026-10-08/09: "акаунти живуть в телефонах …
 * їх має бути видно на платформі, їх стан чи живий чи вже бан, напрям").
 *
 * Grouped direction → phone → accounts, because that is how the rules run: a phone belongs to one
 * direction for ever, an account lives on one phone for ever. Everyone sees everything; anyone signed in
 * registers an account or changes its state (whoever sees a ban first says so — limited and banned need
 * what was seen); an admin shapes the directions. No passwords anywhere.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import type { AccountState, Device, Direction, MarketingAccount } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Card, Chips, Empty, Status } from '../../ui/components'
import { Person } from '../../ui/Person'
import { DirectionChip, useDirectionName, useDirections } from './directions'
import { ProfileLine, ProfilePassCard } from './ProfileReading'

const STATES: AccountState[] = ['warming', 'active', 'limited', 'banned', 'logged_out']

function errorText(e: unknown): string {
  const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
  return d?.hint ?? (e instanceof Error ? e.message : String(e))
}

/** `direction` given — only that direction's accounts (its tab on the direction's page). */
export function AccountsPanel({ direction: fixed }: { direction?: string } = {}) {
  const t = useT()
  const m = t.marketing
  const { api, authEpoch } = useApp()
  const dirs = useDirections()
  const nameOf = useDirectionName()
  const accounts = usePoll(() => api.accounts(), 30000, [api, authEpoch])
  const devices = usePoll(() => api.devices(), 30000, [api, authEpoch])
  const [chosen, setFilter] = useState<string>('all')
  const filter = fixed ?? chosen
  const [problem, setProblem] = useState<string | null>(null)

  const rows = accounts.data?.accounts ?? []
  const phones = (devices.data ?? []) as Device[]
  const bound = phones.filter((p) => p.direction_id)

  const groups = useMemo(() => {
    const shown = dirs.list.filter((d) => filter === 'all' || d.direction_id === filter)
    return shown.map((d) => {
      const dirPhones = bound.filter((p) => p.direction_id === d.direction_id)
      return {
        direction: d,
        phones: dirPhones.map((p) => ({ phone: p, accounts: rows.filter((a) => a.serial === p.serial) })),
        orphans: rows.filter((a) => a.direction_id === d.direction_id && !dirPhones.some((p) => p.serial === a.serial)),
      }
    })
  }, [dirs.list, bound, rows, filter])

  const refresh = () => { accounts.refresh(); dirs.refresh() }

  return (
    <>
      <Card title={m.title} sub={m.sub}>
        {!fixed && <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          <Chips value={filter} onChange={setFilter} options={[
            { id: 'all', label: m.allDirections },
            ...dirs.list.map((d) => ({ id: d.direction_id, label: `${nameOf(d)} · ${d.accounts ?? 0}` })),
          ]} />
        </div>}
        <p className="tiny dim">{m.rules}</p>
        {bound.length === 0 && <p className="tiny warn-text">{m.noBoundPhones}</p>}
        <AddAccount phones={fixed ? bound.filter((p) => p.direction_id === fixed) : bound} directions={dirs.byId} onAdded={refresh} onError={setProblem} />
        <Status text={problem} bad />
      </Card>
      {!fixed && <ProfilePassCard accounts={rows} />}

      {groups.map((g) => (
        <Card key={g.direction.direction_id} title={<DirectionChip direction={g.direction} />}
              sub={m.groupSub(g.phones.length, g.phones.reduce((n, p) => n + p.accounts.length, 0))}>
          <div className="accounts-group">
            {g.phones.length === 0 && <Empty>{m.noPhonesInDirection}</Empty>}
            {g.phones.map(({ phone, accounts: list }) => (
              <div className="accounts-phone" key={phone.device_id}>
                <div className="accounts-phone-head">
                  <b>{phone.name || phone.model || phone.serial}</b>
                  <span className="tiny dim mono">{phone.serial}{phone.hub_id ? ` · ${phone.hub_id}` : ''}</span>
                </div>
                {list.length === 0 && <span className="tiny dim">{m.noAccounts}</span>}
                {list.map((a) => <AccountRow key={a.account_id} account={a} onChanged={refresh} />)}
              </div>
            ))}
            {g.orphans.map((a) => <AccountRow key={a.account_id} account={a} onChanged={refresh} />)}
          </div>
        </Card>
      ))}
    </>
  )
}

function AddAccount({ phones, directions, onAdded, onError }: {
  phones: Device[]
  directions: Map<string, Direction>
  onAdded: () => void
  onError: (s: string | null) => void
}) {
  const t = useT()
  const m = t.marketing
  const nameOf = useDirectionName()
  const { api } = useApp()
  const [email, setEmail] = useState('')
  const [handle, setHandle] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [state, setState] = useState<AccountState>('active')
  const [busy, setBusy] = useState(false)
  const add = async () => {
    setBusy(true)
    onError(null)
    try {
      await api.addAccount({ email, handle: handle.trim() || undefined, device_id: deviceId, state, platform: 'tiktok' })
      setEmail('')
      setHandle('')
      onAdded()
    } catch (e) {
      onError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="account-form">
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={m.emailPlaceholder}
             aria-label={m.emailPlaceholder} />
      <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder={m.handlePlaceholder}
             aria-label={m.handlePlaceholder} />
      <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)} aria-label={m.phone}>
        <option value="">{m.pickPhone}</option>
        {phones.map((p) => (
          <option key={p.device_id} value={p.device_id}>
            {(p.name || p.model || p.serial) + ' — ' + (() => { const d = directions.get(p.direction_id ?? ''); return d ? nameOf(d) : '' })()}
          </option>
        ))}
      </select>
      <select value={state} onChange={(e) => setState(e.target.value as AccountState)} aria-label={m.stateLabel}>
        {(['active', 'warming'] as AccountState[]).map((s) => <option key={s} value={s}>{m.state[s]}</option>)}
      </select>
      <button className="go" disabled={busy || !email.trim() || !deviceId} onClick={() => void add()}>
        {m.add}
      </button>
    </div>
  )
}

function AccountRow({ account: a, onChanged }: { account: MarketingAccount; onChanged: () => void }) {
  const t = useT()
  const m = t.marketing
  const { api } = useApp()
  const [next, setNext] = useState<AccountState | ''>('')
  const [reason, setReason] = useState('')
  const [open, setOpen] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [naming, setNaming] = useState(false)
  const [nick, setNick] = useState('')
  const dirColor = useDirections().byId.get(a.direction_id ?? '')?.color
  const needs = next === 'limited' || next === 'banned'
  const save = async () => {
    if (!next) return
    setProblem(null)
    try {
      await api.editAccount(a.account_id, { state: next, reason })
      setNext('')
      setReason('')
      onChanged()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  const saveNick = async () => {
    setProblem(null)
    try {
      await api.editAccount(a.account_id, { handle: nick })
      setNaming(false)
      setNick('')
      onChanged()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  return (
    <div className="account-row" data-color={dirColor}>
      <span className="handle">
        <Link to={`/accounts/${encodeURIComponent(a.account_id)}`}>{a.label}</Link>
        <span className="dim tiny"> · {a.platform_name}</span>
        {a.email && a.handle && (
          <span className="tiny account-nick">
            {' '}{a.profile_url ? <a className="text-link" href={a.profile_url} target="_blank" rel="noreferrer noopener">@{a.handle}</a> : `@${a.handle}`}
          </span>
        )}
        {!a.handle && !naming && (
          <button className="quiet tiny" onClick={() => setNaming(true)}>{m.addHandle}</button>
        )}
        {naming && (
          <span className="row" style={{ gap: 6, display: 'inline-flex', flexWrap: 'wrap' }}>
            <input value={nick} onChange={(e) => setNick(e.target.value)} placeholder={m.handleOnly}
                   aria-label={m.handleOnly} style={{ minWidth: 0, width: 160 }} autoFocus />
            <button className="tiny" disabled={!nick.trim()} onClick={() => void saveNick()}>{m.save}</button>
          </span>
        )}
      </span>
      <span className={`account-state ${a.state}`}>{m.state[a.state]}</span>
      <div className="meta">
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          <select value={next} onChange={(e) => setNext(e.target.value as AccountState | '')} aria-label={m.changeState}>
            <option value="">{m.changeState}</option>
            {STATES.filter((s) => s !== a.state).map((s) => <option key={s} value={s}>{m.state[s]}</option>)}
          </select>
          {next && <input value={reason} onChange={(e) => setReason(e.target.value)}
                          placeholder={needs ? m.reasonRequired : m.reasonOptional} aria-label={m.reasonOptional}
                          style={{ flex: '1 1 200px', minWidth: 0 }} />}
          {next && <button className="tiny" disabled={needs && !reason.trim()} onClick={() => void save()}>{m.save}</button>}
          <button className="quiet tiny" onClick={() => setOpen(!open)} aria-expanded={open}>{m.history(a.history.length)}</button>
        </div>
        {open && (
          <ol className="account-history">
            {a.history.map((e, i) => (
              <li key={i}>
                <span className={`account-state ${e.state}`}>{m.state[e.state]}</span> · <Person who={e.by} />
                {' · '}{new Date(e.at).toLocaleString()}{e.note ? ` — ${e.note}` : ''}
              </li>
            ))}
          </ol>
        )}
        {problem && <div className="bad-text">{problem}</div>}
        {a.platform === 'tiktok' && <ProfileLine account={a} onChanged={onChanged} />}
      </div>
    </div>
  )
}
